// Groups an expanded timeline's rows under day headers ("Today",
// "Yesterday", "Thu, Sep 4") in the viewer's own zone. Rows render once for
// every viewer, so only the browser knows where a day starts.
//
// The header markup comes from a <template data-timeline-day-template>
// the server renders, keeping its classes in the templates Tailwind
// scans.

import { otherYear } from "./relative_time.js";

const sameYear = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  month: "short",
  day: "numeric",
});

function dayLabel(date, now) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const days = Math.round((today - day) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";

  return (
    date.getFullYear() === now.getFullYear() ? sameYear : otherYear
  ).format(date);
}

function group(list) {
  const template = document.querySelector(
    "template[data-timeline-day-template]",
  );
  if (!template) return;

  list
    .querySelectorAll(":scope > [data-timeline-day]")
    .forEach((h) => h.remove());

  const now = new Date();
  let last = "";
  for (const row of [...list.children]) {
    if (!row.hasAttribute("data-timeline-category")) continue;
    const time = row.querySelector("time[datetime]");
    const date = time && new Date(time.getAttribute("datetime"));
    if (!date || Number.isNaN(date.getTime())) continue;

    const key = date.toDateString();
    if (key === last) continue;
    last = key;

    const header = template.content.firstElementChild.cloneNode(true);
    header.setAttribute("data-timeline-day", key);
    header.textContent = dayLabel(date, now);
    row.before(header);
  }
}

// keepFocusAfterLoadOlder puts focus back after "Load older activity"
// swaps out the button that held it: on the next page's button, or on
// the last row's first link when no older rows remain.
function keepFocusAfterLoadOlder(event, lists) {
  const trigger = event.detail?.requestConfig?.elt;
  if (!trigger?.matches?.("[data-load-older]")) return;
  if (document.activeElement && document.activeElement !== document.body) {
    return;
  }

  for (const list of lists) {
    const next = list.querySelector("[data-load-older]");
    if (next) {
      next.focus();
      return;
    }
    const rows = list.querySelectorAll("[data-timeline-category]");
    rows[rows.length - 1]?.querySelector("a[href]")?.focus();
  }
}

let listening = false;

// listsFor finds the timeline lists a settled swap touched. A "Load
// older activity" swap replaces its own button, so the settled element
// may sit inside the list rather than contain it.
function listsFor(el) {
  const owner = el.closest?.("[data-timeline-days]");
  if (owner) return [owner];

  return [...(el.querySelectorAll?.("[data-timeline-days]") ?? [])];
}

// initTimelineDays groups every timeline list under root, and regroups
// a list after any swap that lands in it.
export function initTimelineDays(root = document) {
  root.querySelectorAll?.("[data-timeline-days]").forEach(group);
  if (listening) return;
  listening = true;

  document.body.addEventListener("htmx:afterSettle", (event) => {
    const lists = listsFor(event.target);
    lists.forEach(group);
    keepFocusAfterLoadOlder(event, lists);
  });
}
